'use client'

import React, { useEffect, useRef, useState, type ReactNode } from 'react'

import { DancingIcon } from '../icons/Dancing'
import { LuggageTagIcon } from '../icons/LuggageTag'
import { MapIcon } from '../icons/Map'
import { RankingIcon } from '../icons/Ranking'
import { cn } from '../ui/cn'
import { AltBlock } from '../ui_patterns/AltBlock'
import { Card } from '../ui_patterns/Card'

const COUNT_UP_DURATION_MS = 1600

/**
 * Ship&Play "Trusted by Travelers Everywhere" stats band. Ported verbatim
 * from the playground — 4 stat cards animate a count-up from zero once the
 * dl scrolls into view. Values are marketing-managed and identical across
 * Ship&Play surfaces, so they stay hardcoded here rather than threading
 * through content props.
 */
export const MonthlyStats = (): React.ReactElement => {
  const [listRef, hasEnteredView] = useInView<HTMLDListElement>()

  return (
    <AltBlock>
      <h1 className="snplay:mb-6 snplay:text-center snplay:text-2xl snplay:font-bold">
        Trusted by <span className="snplay:text-primaryDark"> Travelers Everywhere</span>
      </h1>
      <dl
        ref={listRef}
        className={cn(
          'snplay:grid snplay:grid-cols-1 snplay:gap-4',
          'snplay:xs:grid-cols-2 snplay:lg:grid-cols-4',
        )}
      >
        <MonthlyStatsCard
          animate={hasEnteredView}
          stat="5,120,143"
          label="Bags shipped"
          icon={
            <LuggageTagIcon
              className="snplay:mx-auto snplay:mb-4 snplay:block snplay:size-16"
              aria-hidden="true"
            />
          }
        />
        <MonthlyStatsCard
          animate={hasEnteredView}
          stat="100+"
          label="Countries served"
          icon={
            <MapIcon
              className="snplay:mx-auto snplay:mb-4 snplay:block snplay:size-16"
              aria-hidden="true"
            />
          }
        />
        <MonthlyStatsCard
          animate={hasEnteredView}
          stat="4.7/5"
          label="Customer Rating"
          icon={
            <RankingIcon
              className="snplay:mx-auto snplay:mb-4 snplay:block snplay:size-16"
              aria-hidden="true"
            />
          }
        />
        <MonthlyStatsCard
          animate={hasEnteredView}
          stat="1,014,276"
          label="Satisfied travelers"
          icon={
            <DancingIcon
              className="snplay:mx-auto snplay:mb-4 snplay:block snplay:size-16"
              aria-hidden="true"
            />
          }
        />
      </dl>
    </AltBlock>
  )
}

interface MonthlyStatsCardProps {
  stat?: string
  label?: string
  icon?: ReactNode
  animate?: boolean
}

const MonthlyStatsCard = ({
  icon,
  stat,
  label,
  animate = false,
}: MonthlyStatsCardProps): React.ReactElement => {
  const countedStat = useCountUpStat(stat, animate)

  return (
    <Card className="snplay:flex snplay:flex-col snplay:px-4 snplay:py-8 snplay:text-center">
      {icon}
      <dt className="snplay:order-2 snplay:text-base snplay:font-medium">{label}</dt>
      <dd
        className={cn(
          'snplay:order-1 snplay:mb-1 snplay:text-3xl snplay:text-primaryDark',
          'snplay:font-bold snplay:tabular-nums snplay:@3xs:text-4xl',
        )}
      >
        {countedStat === stat ? (
          stat
        ) : (
          <>
            <span aria-hidden="true">{countedStat}</span>
            <span className="snplay:sr-only">{stat}</span>
          </>
        )}
      </dd>
    </Card>
  )
}

/**
 * Reports whether the observed element has scrolled into view. Stays true
 * once it has, so the count-up only ever runs on the first pass.
 */
const useInView = <T extends HTMLElement>(): readonly [React.RefObject<T | null>, boolean] => {
  const ref = useRef<T>(null)
  const [hasEnteredView, setHasEnteredView] = useState(false)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    if (typeof IntersectionObserver === 'undefined') {
      setHasEnteredView(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        setHasEnteredView(true)
        observer.disconnect()
      },
      { threshold: 0.25 },
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return [ref, hasEnteredView] as const
}

/**
 * Counts a stat's numeric portion up from zero once `animate` turns on,
 * preserving any surrounding text ("4.7/5", "100+") and number
 * formatting. Non-numeric stats are returned untouched.
 */
const useCountUpStat = (stat: string | undefined, animate: boolean): string | undefined => {
  const parsed = parseStat(stat)
  const [progress, setProgress] = useState(0)
  const isAnimatable = Boolean(parsed) && animate

  useEffect(() => {
    if (!isAnimatable) return

    if (prefersReducedMotion()) {
      setProgress(1)
      return
    }

    let start: number | null = null
    let frame = requestAnimationFrame(function step(timestamp) {
      start ??= timestamp
      const elapsed = timestamp - start
      const nextProgress = Math.min(elapsed / COUNT_UP_DURATION_MS, 1)
      setProgress(easeOutCubic(nextProgress))
      if (nextProgress < 1) frame = requestAnimationFrame(step)
    })

    return () => cancelAnimationFrame(frame)
  }, [isAnimatable])

  if (!parsed) return stat

  const { prefix, suffix, value, decimals, useGrouping } = parsed

  const counted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping,
  }).format(value * progress)

  return `${prefix}${counted}${suffix}`
}

interface ParsedStat {
  prefix: string
  suffix: string
  value: number
  decimals: number
  useGrouping: boolean
}

const parseStat = (stat: string | undefined): ParsedStat | null => {
  if (!stat) return null

  const match = stat.match(/\d[\d,]*(?:\.\d+)?/)
  if (!match || match.index === undefined) return null

  const [raw] = match
  const value = Number(raw.replaceAll(',', ''))
  if (!Number.isFinite(value)) return null

  return {
    prefix: stat.slice(0, match.index),
    suffix: stat.slice(match.index + raw.length),
    value,
    decimals: raw.split('.')[1]?.length ?? 0,
    useGrouping: raw.includes(','),
  }
}

const easeOutCubic = (progress: number): number => 1 - (1 - progress) ** 3

const prefersReducedMotion = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}
